import { useState, useEffect } from 'react';
import { Building2, Save, Mail, Phone, MapPin, AlertCircle, CheckCircle, Loader2, Lock, Key } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { getUser } from '@/lib/auth';

interface OrganizationAccount {
  id: string;
  name: string;
  organization_number: string;
  contact_email: string;
  contact_phone?: string;
  address?: string;
  type: 'study_association' | 'member';
}

export default function Account() {
  const currentUser = getUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [account, setAccount] = useState<OrganizationAccount | null>(null);

  // Editable fields (only for allowed users)
  const [contactPhone, setContactPhone] = useState('');
  const [address, setAddress] = useState('');

  // Password change
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    loadAccount();
  }, [currentUser?.id]);

  const loadAccount = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      setError('');

      let accountData: OrganizationAccount | null = null;

      if (currentUser.role === 'systemadmin' || currentUser.role === 'admin') {
        // Studieforbund - fetch study association
        if (currentUser.study_association_id) {
          const response = await authenticatedFetch('/api/admin/get-all-study-associations', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          });
          const data = await response.json();

          if (data.success) {
            const myOrg = data.associations.find((org: any) =>
              org.id === currentUser.study_association_id
            );
            if (myOrg) {
              accountData = {
                id: myOrg.id,
                name: myOrg.name,
                organization_number: myOrg.organization_number || '',
                contact_email: myOrg.contact_email || '',
                contact_phone: myOrg.contact_phone || '',
                address: myOrg.address || '',
                type: 'study_association'
              };
            }
          }
        }
      } else if (currentUser.role === 'member') {
        // Member organization - fetch member
        if (currentUser.member_id) {
          const response = await authenticatedFetch('/api/admin/get-members', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({})
          });
          const data = await response.json();

          if (data.success) {
            const myOrg = data.members.find((m: any) => m.id === currentUser.member_id);
            if (myOrg) {
              accountData = {
                id: myOrg.id,
                name: myOrg.organization_name,
                organization_number: myOrg.organization_number || '',
                contact_email: myOrg.contact_email || '',
                contact_phone: myOrg.contact_phone || '',
                address: myOrg.address || '',
                type: 'member'
              };
            }
          }
        }
      }

      if (accountData) {
        setAccount(accountData);
        setContactPhone(accountData.contact_phone || '');
        setAddress(accountData.address || '');
      } else {
        setError('Kunne ikke finne organisasjonskonto');
      }
    } catch (err: any) {
      setError(err.message || 'Kunne ikke laste konto');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!account || !currentUser) return;

    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const updates: any = {};
      if (contactPhone !== (account.contact_phone || '')) updates.contact_phone = contactPhone;
      if (address !== (account.address || '')) updates.address = address;

      if (Object.keys(updates).length === 0) {
        setSuccess('Ingen endringer å lagre');
        return;
      }

      let endpoint = '';
      let body: any = {};

      if (account.type === 'study_association') {
        endpoint = '/api/admin/update-study-association';
        body = {
          admin_id: currentUser.id,
          study_association_id: account.id,
          ...updates
        };
      } else {
        endpoint = '/api/admin/update-member';
        body = {
          member_id: account.id,
          ...updates
        };
      }

      const response = await authenticatedFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Kunne ikke oppdatere');
      }

      setSuccess('Kontoinformasjon oppdatert');
      await loadAccount();
    } catch (err: any) {
      setError(err.message || 'Kunne ikke oppdatere konto');
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;

    if (newPassword !== confirmPassword) {
      setError('Passordene stemmer ikke overens');
      return;
    }

    if (newPassword.length < 8) {
      setError('Passordet må være minst 8 tegn');
      return;
    }

    try {
      setChangingPassword(true);
      setError('');
      setSuccess('');

      const response = await authenticatedFetch('/api/admin/change-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: currentUser.id,
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Kunne ikke endre passord');
      }

      setSuccess('Passordet er endret');
      setShowPasswordForm(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.message || 'Kunne ikke endre passord');
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-kf-primary" />
      </div>
    );
  }

  if (!account) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="text-center text-red-600">
          <AlertCircle className="h-12 w-12 mx-auto mb-4" />
          <p>{error || 'Kunne ikke laste organisasjonskonto'}</p>
        </div>
      </div>
    );
  }

  const higherLevelName = account.type === 'study_association' ? 'systemadministrator' : 'studieforbundet';

  return (
    <div className="space-y-6">
      {/* Organization Account */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <Building2 className="h-6 w-6 text-kf-primary" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Organisasjonskonto</h2>
              <p className="text-sm text-gray-600 mt-1">
                {account.type === 'study_association' ? 'Studieforbund' : 'Medlemsorganisasjon'}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-400 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-400 flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-green-800">{success}</p>
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-6">
            {/* Locked fields */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                <Lock className="h-4 w-4" />
                <span>Disse feltene kan kun endres av {higherLevelName}</span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organisasjonsnavn
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    className="pl-10 w-full px-4 py-2 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                    value={account.name}
                    disabled
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organisasjonsnummer
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                  value={account.organization_number || 'Ikke oppgitt'}
                  disabled
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kontakt e-post
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    className="pl-10 w-full px-4 py-2 border rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                    value={account.contact_email || 'Ikke oppgitt'}
                    disabled
                  />
                </div>
              </div>
            </div>

            {/* Editable fields */}
            <div className="space-y-4">
              <div className="text-sm text-gray-500 mb-2">
                Disse feltene kan du redigere selv
              </div>

              <div>
                <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700 mb-2">
                  Telefon
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="tel"
                    id="contactPhone"
                    className="pl-10 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    placeholder="Legg til telefonnummer"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  Adresse
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                  <textarea
                    id="address"
                    rows={3}
                    className="pl-10 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Legg til adresse"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-kf-primary text-white rounded-lg hover:bg-kf-secondary focus:ring-2 focus:ring-offset-2 focus:ring-kf-primary disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    Lagrer...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    Lagre endringer
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Password Change */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <Key className="h-6 w-6 text-kf-primary" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Endre passord</h2>
              <p className="text-sm text-gray-600 mt-1">
                Endre passordet for din brukerkonto
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {!showPasswordForm ? (
            <button
              onClick={() => setShowPasswordForm(true)}
              className="px-4 py-2 text-kf-primary border border-kf-primary rounded-lg hover:bg-kf-primary hover:text-white transition-colors"
            >
              Endre passord
            </button>
          ) : (
            <form onSubmit={handlePasswordChange} className="space-y-4 max-w-md">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Nåværende passord
                </label>
                <input
                  type="password"
                  id="currentPassword"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                />
              </div>

              <div>
                <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Nytt passord
                </label>
                <input
                  type="password"
                  id="newPassword"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                />
                <p className="text-xs text-gray-500 mt-1">Minst 8 tegn</p>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Bekreft nytt passord
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-kf-primary focus:border-transparent"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="px-4 py-2 bg-kf-primary text-white rounded-lg hover:bg-kf-secondary disabled:opacity-50 flex items-center gap-2"
                >
                  {changingPassword ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Endrer...
                    </>
                  ) : (
                    'Endre passord'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Avbryt
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
